import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { X, ChevronUp, Loader2, ChevronLeft, ChevronRight, Layers } from 'lucide-react';
import { getPublicEpisode, adminPreviewEpisode } from '../services/cms';

// preview=true -> uses admin endpoint (shows drafts)
const EpisodeReader = ({ preview = false }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [scrollPct, setScrollPct] = useState(0);
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    setData(null); setError(false);
    const fetcher = preview ? adminPreviewEpisode : getPublicEpisode;
    fetcher(slug).then(setData).catch(() => setError(true));
  }, [slug, preview]);

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setScrollPct(h > 0 ? Math.round((window.scrollY / h) * 100) : 0);
      setShowTop(window.scrollY > 600);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lightly preload next images (beyond the eager ones) for smoother scroll
  useEffect(() => {
    if (!data?.images) return;
    data.images.slice(2, 6).forEach((img) => { const p = new Image(); p.src = img.image_url; });
  }, [data]);

  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#141414] text-white">
      <p className="font-display text-xl">Épisode introuvable ou non publié.</p>
      <Link to="/series" className="text-primary font-800">Retour aux séries</Link>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-[#141414]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const { episode, serie, images, prev, next } = data;
  const readerBase = preview ? '/apercu' : '/lire';

  return (
    <div className="bg-[#141414] min-h-screen">
      {/* progress bar */}
      <div className="fixed top-0 left-0 right-0 h-1.5 bg-black/40 z-50">
        <div className="h-full bg-primary transition-[width] duration-150" style={{ width: `${scrollPct}%` }} />
      </div>

      {/* top bar */}
      <div className="fixed top-1.5 left-0 right-0 z-40 bg-[#141414]/85 backdrop-blur">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <button onClick={() => (serie ? navigate(`/serie/${serie.slug}`) : navigate('/series'))} className="flex items-center gap-2 text-white/90 font-700 hover:text-white shrink-0"><X className="w-5 h-5" /> <span className="hidden sm:inline">Épisode</span></button>
          <div className="text-center min-w-0">
            <p className="font-display font-600 text-white text-sm truncate">{episode.titre}</p>
            {serie && <p className="text-white/50 text-xs truncate">{serie.titre}</p>}
          </div>
          {serie ? (
            <Link to={`/serie/${serie.slug}`} className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 shrink-0"><Layers className="w-4 h-4 text-white" /></Link>
          ) : <div className="w-9 shrink-0" />}
        </div>
      </div>

      {preview && (
        <div className="fixed top-14 left-1/2 -translate-x-1/2 z-40 px-4 py-1.5 rounded-full bg-secondary text-secondary-foreground font-800 text-xs shadow-lg">
          Mode aperçu — statut : {episode.statut}
        </div>
      )}

      {/* vertical strip — NO gaps between images, ratio preserved */}
      <div className="max-w-2xl mx-auto pt-16">
        {images.length === 0 ? (
          <div className="py-24 text-center text-white/50 font-600">Aucune image dans cet épisode.</div>
        ) : (
          <div className="flex flex-col">
            {images.map((img, i) => (
              <img
                key={img.id}
                src={img.image_url}
                alt={img.texte_alternatif || `planche ${i + 1}`}
                width={img.largeur || undefined}
                height={img.hauteur || undefined}
                loading={i < 2 ? 'eager' : 'lazy'}
                decoding="async"
                className="w-full h-auto block align-top"
                style={{ aspectRatio: img.largeur && img.hauteur ? `${img.largeur} / ${img.hauteur}` : undefined }}
              />
            ))}
          </div>
        )}

        {/* End card + episode navigation */}
        <div className="px-4 py-12 text-center">
          <div className="inline-block w-16 h-1 rounded bg-primary mb-6" />
          <h3 className="font-display font-700 text-2xl text-white mb-2">Fin de l’épisode</h3>
          {episode.numero != null && <p className="text-white/60 font-600 mb-6">Épisode {episode.numero} — {episode.titre}</p>}
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-stretch">
            {prev ? (
              <Link to={`${readerBase}/${prev.slug}`} className="flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white/10 text-white font-800 hover:bg-white/20 transition-colors"><ChevronLeft className="w-4 h-4" /> Épisode précédent</Link>
            ) : <span className="px-6 py-3 rounded-full bg-white/5 text-white/30 font-800 cursor-not-allowed">Aucun précédent</span>}
            {serie && <Link to={`/serie/${serie.slug}`} className="flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white/10 text-white font-800 hover:bg-white/20 transition-colors"><Layers className="w-4 h-4" /> La série</Link>}
            {next ? (
              <Link to={`${readerBase}/${next.slug}`} className="flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary text-white font-800 hover:scale-105 transition-transform">Épisode suivant <ChevronRight className="w-4 h-4" /></Link>
            ) : <span className="px-6 py-3 rounded-full bg-white/5 text-white/30 font-800 cursor-not-allowed">Aucun suivant</span>}
          </div>
        </div>
      </div>

      {showTop && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center shadow-xl hover:scale-110 transition-transform"><ChevronUp className="w-6 h-6" /></button>
      )}
    </div>
  );
};

export default EpisodeReader;
